import React from "react";
import { Helmet } from "react-helmet-async";
import { Shield, Award, MapPin, Clock, Users, FileText } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { hreflangLinks, isFrPath, enTwinFor, SITE_BASE_URL } from "@/i18n/paths";
import { useLocation } from "react-router-dom";
import { FadeIn, MaskedReveal } from "@/components/Reveal";

export default function About() {
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const enPath = enTwinFor(pathname);
  const fr = lang === "fr";
  const certifications = fr
    ? [
        { icon: Shield, title: "Licencié BSP", desc: "Bureau de la sécurité privée du Québec — chaque agent, chaque site." },
        { icon: Award, title: "Aligné ISO 18788", desc: "Système de gestion des opérations de sécurité privée." },
        { icon: FileText, title: "Conforme CNESST", desc: "Conformité complète en santé et sécurité du travail." },
        { icon: Clock, title: "Centre de commandement 24/7", desc: "Répartition en temps réel depuis notre siège de Montréal." },
        { icon: MapPin, title: "17 régions couvertes", desc: "Déploiement en moins de 45 minutes partout au Québec." },
        { icon: Users, title: "1 240+ agents", desc: "Bilingues FR/EN. Vérifiés. Vigilants." },
      ]
    : [
        { icon: Shield, title: "BSP Licensed", desc: "Bureau de la sécurité privée du Québec — every officer, every site." },
        { icon: Award, title: "ISO 18788 Aligned", desc: "Private security operations management system." },
        { icon: FileText, title: "CNESST Compliant", desc: "Full workplace health & safety adherence." },
        { icon: Clock, title: "24/7 Command Centre", desc: "Real-time dispatch from our Montreal HQ." },
        { icon: MapPin, title: "17 Regions Covered", desc: "Sub-45-minute deployment across Quebec." },
        { icon: Users, title: "1,240+ Officers", desc: "Bilingual FR/EN. Vetted. Vigilant." },
      ];
  return (
    <>
      <Helmet>
        <title>{t("meta.about_title")}</title>
        <meta name="description" content={t("meta.about_desc")} />
        <link rel="canonical" href={`${SITE_BASE_URL}${isFrPath(pathname) ? pathname : enPath}`} />
        {(hreflangLinks(enPath) || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>

      <section className="px-6 md:px-10 pt-24 md:pt-32 pb-24 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-amber-500" />
          {t("nav.about")}
        </div>
        <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-white max-w-5xl">
          <MaskedReveal>
            <span>{t("about.title")}</span>
          </MaskedReveal>
        </h1>
        <FadeIn delay={0.2}>
          <p className="mt-10 text-lg md:text-2xl text-zinc-300 max-w-3xl leading-relaxed">{t("about.body")}</p>
        </FadeIn>
      </section>

      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {fr ? "Conformité et accréditations" : "Compliance & credentials"}</div>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {certifications.map((c, i) => {
            const Icon = c.icon;
            return (
              <FadeIn key={i} delay={i * 0.05}>
                <div className="border border-white/10 p-8 hover:border-amber-500 transition-colors duration-300 h-full">
                  <Icon className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
                  <h3 className="font-display uppercase text-2xl tracking-tight text-white mt-6">{c.title}</h3>
                  <p className="mt-3 text-zinc-400 text-sm">{c.desc}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white">{fr ? "Notre protocole de formation" : "Our training protocol"}</h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-8 text-lg text-zinc-400 max-w-3xl leading-relaxed">
            {fr
              ? "Avant d'entrer en poste, chaque agent complète plus de 60 heures de formation pré-déploiement : certification BSP du Québec, gradation du recours à la force, désescalade verbale, bâton ASP, premiers soins RCR-C, protocole radio, documentation d'incidents et formation propre au site. Chaque agent est bilingue au niveau CECR B2 minimum et suit une formation de mise à niveau annuelle sur tous les modules."
              : "Before an officer touches a post, they complete 60+ hours of pre-deployment training including Quebec BSP certification, use-of-force scaling, verbal de-escalation, ASP baton, first-aid CPR-C, radio protocol, incident documentation, and site-specific onboarding. Every guard is bilingual to CEFR B2 minimum and is refreshed annually across all core modules."}
          </p>
        </FadeIn>
      </section>
    </>
  );
}
